import { DefaultError, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useQueryToast } from 'hooks';
import i18next from 'i18next';
import { apiClient } from 'utils';
import {
  IResponseHandlerOptions,
  useResponseHandler,
} from 'utils/service/responseHandler';
import { parseAgreementType } from 'utils/components/contract-template/agreement/agreementTemplate.utils';
import { ITemplatesLightListItem } from '../types';
import {
  AGREEMENT_TYPE,
  IAgreementTemplateResponse,
  IAgreementTemplatetAggregator,
  IUpdateAgreementTemplateResponse,
} from './types';

export type AgreementTemplateListKind = 'text' | 'paragraph';

export const useGetAgreementTemplateLightList = (enabled?: boolean) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<ITemplatesLightListItem[], DefaultError>({
    queryKey: ['agreement-template-light-list'],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `AgreementTemplate/LightList`,
      });

      return responseHandler(response, {
        groupName: `useGetAgreementTemplateLightList`,
        notFoundPath: '/templates',
      });
    },
    enabled: enabled,
  });
};

export const useAgreementTemplatesLightListByKind = (
  kind: AgreementTemplateListKind,
  enabled = true
) => {
  const { responseHandler } = useResponseHandler();

  return useQuery<ITemplatesLightListItem[], DefaultError>({
    queryKey: ['agreement-template-light-list', kind],
    enabled,
    staleTime: 60_000,
    async queryFn() {
      const lightResponse = await apiClient({
        method: 'GET',
        url: 'AgreementTemplate/LightList',
      });

      const lightList = await responseHandler<ITemplatesLightListItem[]>(
        lightResponse,
        {
          groupName: 'useAgreementTemplatesLightListByKind-light',
        }
      );

      const templatesWithType = await Promise.all(
        lightList.map(async item => {
          const response = await apiClient({
            method: 'GET',
            url: `AgreementTemplate/${item.id}`,
          });

          const template = await responseHandler<IAgreementTemplateResponse>(
            response,
            {
              groupName: `useAgreementTemplatesLightListByKind-${item.id}`,
            }
          );

          return {
            item,
            agreementType: parseAgreementType(template.type),
          };
        })
      );

      return templatesWithType
        .filter(({ agreementType }) =>
          kind === 'paragraph'
            ? agreementType === AGREEMENT_TYPE.text
            : agreementType === AGREEMENT_TYPE.text ||
              agreementType === AGREEMENT_TYPE.select
        )
        .map(({ item }) => item);
    },
  });
};

export const useGetAgreementTemplate = (
  id: string,
  options?: IResponseHandlerOptions<IAgreementTemplateResponse>,
  enabled?: boolean,
  retry?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IAgreementTemplateResponse, DefaultError>({
    queryKey: ['agreement-template', id],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `AgreementTemplate/${id}`,
      });

      return responseHandler(response, {
        groupName: `useGetAgreementTemplate - ${id}`,
        notFoundPath: '/templates',
        ...options,
      });
    },
    retry,
    enabled,
  });
};

export const usePostAgreementTemplate = () => {
  const { responseHandler } = useResponseHandler();
  const queryClient = useQueryClient();
  const { loading, dismiss, success } = useQueryToast(
    'usePostAgreementTemplate'
  );
  return useMutation<string, DefaultError, IAgreementTemplatetAggregator>({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.create.loading', {
          what: '',
        })
      );
      const response = await apiClient({
        method: 'POST',
        url: 'AgreementTemplate',
        body: { agreementTemplateAggregator: body },
      });

      return responseHandler(response, {
        groupName: 'usePostAgreementTemplate',
      });
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['agreement-template-light-list'] });
      success(
        i18next.t('service-message.create.success', {
          what: '',
        })
      );
    },
    onError: dismiss,
  });
};

export const usePutAgreementTemplate = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const queryClient = useQueryClient();
  const { loading, dismiss, success } = useQueryToast(
    'usePutAgreementTemplate'
  );
  return useMutation<
    IUpdateAgreementTemplateResponse,
    DefaultError,
    IAgreementTemplatetAggregator
  >({
    mutationFn: async body => {
      loading(
        i18next.t('service-message.update.loading', {
          what: '',
        })
      );
      const response = await apiClient({
        method: 'PUT',
        url: 'AgreementTemplate',
        body: { agreementTemplateId: id, agreementTemplateAggregator: body },
      });

      return responseHandler(response, {
        groupName: 'usePutAgreementTemplate',
      });
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['agreement-template-light-list'] });
      queryClient.invalidateQueries({ queryKey: ['agreement-template', id] });
      success(
        i18next.t('service-message.update.success', {
          what: data.title,
        })
      );
    },
    onError: dismiss,
  });
};

export const useDeleteAgreementTemplate = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const queryClient = useQueryClient();
  const { loading, dismiss, success } = useQueryToast(
    'useDeleteAgreementTemplate'
  );
  return useMutation<void, DefaultError>({
    mutationFn: async () => {
      loading(
        i18next.t('service-message.remove.loading', {
          what: '',
        })
      );
      const response = await apiClient({
        method: 'DELETE',
        url: `AgreementTemplate/${id}`,
      });

      return responseHandler(response, {
        groupName: 'useDeleteAgreementTemplate',
      });
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: ['agreement-template-light-list'] });
      success(
        i18next.t('service-message.remove.success', {
          what: '',
        })
      );
    },
    onError: dismiss,
  });
};
